import { List, Stack, Text } from "@chakra-ui/react";
import { X } from "lucide-react";
import { useState } from "react";
import { Banner } from "~/components/ui/banner";
import { IconButton } from "~/components/ui/icon-button";
import { isHandledByGlobalHandler } from "~/utils/trpcError";
import { isServerUnreachable } from "../logic/isServerUnreachable";
import {
  type ResolvedErrorCopy,
  resolveErrorCopy,
} from "../logic/resolveErrorCopy";
import { ErrorActions } from "./ErrorActions";
import { ServerUnreachableNotice } from "./ServerUnreachableNotice";

export interface HandledErrorAlertProps {
  /**
   * Any error — handled or not. Renders nothing when null/undefined, or when a
   * global interceptor has already reported it.
   */
  error: unknown;
  /**
   * Headline for a failure we have no specific copy for — "Couldn't load
   * replicas". A code the registry knows keeps its own, better title.
   * This is the one you usually want.
   */
  fallbackTitle?: string;
  /** Hard override of the title, registry entry or not. Rare. */
  title?: string;
  /**
   * Show every remediation tip as a list rather than folding the first into
   * the description. Inline alerts have the room; toasts don't.
   */
  showAllTips?: boolean;
  /** Passed to the banner, for a surface that has to place it. */
  className?: string;
  /**
   * Runs the failed request again. Used only when nothing answered at all,
   * where the screen becomes a wait that settles itself rather than an error
   * somebody has to act on.
   */
  onRetry?: () => void;
  /**
   * Whether the reader may put this one away. On by default: an inline alert
   * outlives the moment it describes, and a reader who has read it and cannot
   * act on it should not have to keep it on screen to carry on working.
   *
   * Dismissing hides the ALERT, never the state — the query is still failed,
   * the form still rejected — and a DIFFERENT failure brings the alert
   * straight back, so putting one away can never hide the next one.
   *
   * Turn it off where the alert is the only thing explaining why a control in
   * front of the reader will not work.
   */
  dismissible?: boolean;
  /** Told when the reader dismisses it, for a caller that keeps its own state. */
  onDismiss?: () => void;
}

/** No error has been dismissed yet — distinct from having dismissed `null`. */
const NOTHING_DISMISSED = Symbol("nothing-dismissed");

/**
 * The inline counterpart to `showErrorToast` — same copy, same affordances,
 * rendered in place instead of over the top, as a Ledger `Banner` (err, `sm`;
 * DEVIATIONS "Sign in (7a)", row 12): the title, then the description, the
 * tips, the docs link and the trace id, and Dismiss at the end.
 *
 * Use this wherever the error belongs to a region of the page rather than to a
 * moment: a panel that failed to load, a form that was rejected, a step that
 * can't proceed. A toast is for something that just happened; an alert is for
 * something that is still true.
 */
export function HandledErrorAlert({
  error,
  title,
  fallbackTitle,
  showAllTips = true,
  className,
  onRetry,
  dismissible = true,
  onDismiss,
}: HandledErrorAlertProps) {
  // Keyed on the failure ITSELF rather than a boolean, so the alert returns
  // the moment a different one arrives. A boolean would stay true across the
  // next failure and silently swallow it.
  const [dismissed, setDismissed] = useState<unknown>(NOTHING_DISMISSED);

  if (!error) return null;

  // Already surfaced by a global interceptor in `utils/api.tsx` — the upgrade
  // modal, or one of its bespoke toasts. `showErrorToast` has always made this
  // check; the alert did not, so a plan-limit refusal drew "Something went
  // wrong / We've been notified" underneath the modal that was busy explaining
  // it properly.
  if (isHandledByGlobalHandler(error)) return null;

  // NOTHING ANSWERED. Said as a wait rather than as a fault, because that is
  // what it is: the request never left the browser, so "we've been notified"
  // is a promise nobody kept and the trace id names a trace that does not
  // exist. Ahead of the registry, since no code arrived to look up.
  if (isServerUnreachable(error)) {
    return <ServerUnreachableNotice onRetry={onRetry} className={className} />;
  }

  // Put away by the reader, and it is still the same failure. Deliberately
  // BELOW the unreachable branch: that one is a wait that settles itself and
  // retries on its own, so there is nothing there to dismiss.
  if (dismissible && dismissed === error) return null;

  // One parse, and the same two rules the toast renders — whose headline
  // wins, and which tips add to the description rather than repeating it.
  const copy = resolveErrorCopy({ error, title, fallbackTitle });

  return (
    <Banner
      tone="err"
      // Title, then the body under it at every width: the body is several
      // lines (description, tips, actions), never a run-on after the title.
      stacked
      flexWrap="nowrap"
      size="sm"
      className={className}
      title={copy.title}
      action={
        dismissible ? (
          <IconButton
            aria-label="Dismiss"
            title="Dismiss"
            variant="ghost"
            size="2xs"
            color="inherit"
            onClick={() => {
              setDismissed(error);
              onDismiss?.();
            }}
          >
            <X aria-hidden="true" />
          </IconButton>
        ) : undefined
      }
    >
      <HandledErrorBody copy={copy} showAllTips={showAllTips} />
    </Banner>
  );
}

/** Everything under the banner's title: the description, the tips, the docs
 *  link and the trace id. Nothing at all when there is none of them. */
function HandledErrorBody({
  copy,
  showAllTips,
}: {
  copy: ResolvedErrorCopy;
  showAllTips: boolean;
}) {
  const { tips } = copy;
  const hasBody =
    Boolean(copy.description) ||
    tips.length > 0 ||
    Boolean(copy.docsUrl) ||
    Boolean(copy.traceId);
  if (!hasBody) return null;

  return (
    <Stack gap="var(--space-1)" width="full">
      {copy.description ? <Text>{copy.description}</Text> : null}

      {showAllTips && tips.length > 0 && (
        <List.Root gap="var(--space-1)" paddingLeft="var(--space-4)">
          {/* Index key: tips are server-supplied prose, so two can be
              identical and collide as keys. Their order is fixed. */}
          {tips.map((tip, index) => (
            <List.Item key={index}>{tip}</List.Item>
          ))}
        </List.Root>
      )}
      {!showAllTips && tips[0] && <Text>{tips[0]}</Text>}

      <ErrorActions
        docsUrl={copy.docsUrl}
        traceId={copy.traceId}
        color="inherit"
        accentColor="inherit"
      />
    </Stack>
  );
}
